import { useTranslation } from "react-i18next";
import { useAppDispatch, useSelector } from "../../../../redux-store";
import { useEffect, useState } from "react";
import {
  cancelShareCargo,
  loadCargoSharedToStatus,
} from "../../../../ducks/app/cargo-dialog";
import { selectCargoSharedToStatus } from "../../../../ducks/app/cargo-dialog/selectors";
import { DoraButton } from "dora-shared/frontend";
import GenericPromptDialog from "../../GenericPromptDialog/GenericPromptDialog";
import ShareCargoDialog from "./ShareCargoDialog";

const ContractorInfoSection = ({ cargoId }: { cargoId: string }) => {
  const { t } = useTranslation(["shipmentModal"]);
  const dispatch = useAppDispatch();
  const [shouldShowShareDialog, setShouldShowShareDialog] = useState(false);
  const [shouldShowCancelShareDialog, setShouldShowCancelShareDialog] =
    useState(false);
  const [cancelRequestInProgress, setCancelRequestInProgress] = useState(false);
  const cargoSharedToStatus = useSelector(selectCargoSharedToStatus);

  useEffect(() => {
    dispatch(loadCargoSharedToStatus(cargoId));
  }, [dispatch, cargoId]);

  const onConfirmCancelShare = async () => {
    setCancelRequestInProgress(true);
    dispatch(
      cancelShareCargo({ cargoId, contractId: cargoSharedToStatus.contractId! })
    )
      .unwrap()
      .then(() => setShouldShowCancelShareDialog(false))
      .catch(() => {
        // The cancelShareCargo thunk has already reported it and shown the error toast.
      })
      .finally(() => setCancelRequestInProgress(false));
  };

  return (
    <>
      {shouldShowShareDialog && (
        <ShareCargoDialog
          cargoId={cargoId}
          onClose={() => setShouldShowShareDialog(false)}
        />
      )}
      {shouldShowCancelShareDialog && (
        <GenericPromptDialog
          title={t("shipmentModal:shareCargo.cancelTitle")}
          text={t("shipmentModal:shareCargo.cancelText")}
          onCancelClick={() => setShouldShowCancelShareDialog(true)}
          onContinueClick={onConfirmCancelShare}
          continueButtonColor={"error"}
          buttonsDisabled={cancelRequestInProgress}
          cancelText={t("shipmentModal:shareCargo.doNotCancelButtonLabel")}
          continueText={t("shipmentModal:shareCargo.confirmCancelButtonLabel")}
        />
      )}
      <div className="shipment-dialog__content-card">
        <h3 className="shipment-dialog__content-card-title">
          {t("shipmentModal:shareCargo.contractorInfoTitle")}
        </h3>
        <div
          style={{ paddingBottom: 42 }}
          className="shipment-dialog__content-card-right-input-list"
        >
          {!cargoSharedToStatus && (
            <DoraButton
              variant={"primary-filled"}
              onClick={() => setShouldShowShareDialog(true)}
            >
              {t("shipmentModal:shareCargo.shareCargo")}
            </DoraButton>
          )}
          {cargoSharedToStatus?.status === "ACCEPTED" && (
            <div className="text-gray-500">
              {t("shipmentModal:shareCargo.accepted", {
                subcontractor: cargoSharedToStatus.subcontractorOrgName,
              })}
            </div>
          )}
          {cargoSharedToStatus?.status === "PENDING" && (
            <div className="flex flex-col gap-4">
              <div className="text-gray-500">
                {t("shipmentModal:shareCargo.pending", {
                  subcontractor: cargoSharedToStatus.subcontractorOrgName,
                })}
              </div>
              <DoraButton
                variant={"error"}
                onClick={() => setShouldShowCancelShareDialog(false)}
              >
                {t("shipmentModal:shareCargo.abortShareCargo")}
              </DoraButton>
            </div>
          )}
          {cargoSharedToStatus?.status === "REJECTED" && (
            <div className="flex flex-col gap-4">
              <div className="text-gray-500">
                {t("shipmentModal:shareCargo.rejected", {
                  subcontractor: cargoSharedToStatus.subcontractorOrgName,
                })}
              </div>
              <DoraButton
                variant={"primary-filled"}
                onClick={() => setShouldShowShareDialog(true)}
              >
                {t("shipmentModal:shareCargo.moreSharingOptions")}
              </DoraButton>
            </div>
          )}
          {cargoSharedToStatus?.status === "NONE" && (
            <div className="flex flex-col gap-4">
              <DoraButton
                variant={"primary-filled"}
                onClick={() => setShouldShowShareDialog(true)}
              >
                {t("shipmentModal:shareCargo.shareCargo")}
              </DoraButton>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ContractorInfoSection;
